import { useEffect, useState } from 'react'
import { Save } from 'lucide-react'
import {
  useClassification,
  useSaveClassification,
  useClassificationTargets,
  useClassificationStatus,
  useActivateClassification,
  useUpdateInbox,
  ClassificationRequestError,
  queryKeys,
} from '@/api'
import { useQueryClient } from '@tanstack/react-query'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { InstructionOverlays } from '@/components/instructions/instruction-overlays'
import { MailboxPermissions } from './mailbox-permissions'
import { Preview } from './preview'
import { classificationRequestMessage } from './errors'
import { normalizeConfig, type ClassificationConfig } from './config'

export type MailboxSection =
  'instructions' | 'permissions' | 'preview' | 'processing'
type Props = {
  inboxId: string
  gmail?: boolean
  onDirtyChange?: (inboxId: string, dirty: boolean) => void
  section?: MailboxSection
  starterInstructions?: boolean
  onSectionChange?: (section: MailboxSection) => void
}

export function ClassificationPanel(props: Props) {
  const query = useClassification(props.inboxId)
  if (query.isLoading)
    return (
      <div
        role="status"
        aria-label="Loading mailbox settings"
        className="space-y-4"
      >
        <Skeleton className="h-32 w-full" />
        <Skeleton className="h-40 w-full" />
      </div>
    )
  if (!query.data)
    return (
      <div role="alert" className="space-y-3 rounded-lg border p-4">
        <p>Mailbox settings could not load.</p>
        <Button variant="outline" onClick={() => query.refetch()}>
          Try again
        </Button>
      </div>
    )
  return (
    <MailboxEditor
      {...props}
      initial={normalizeConfig(query.data)}
      reload={async () => {
        const response = await query.refetch()
        if (response.isError) throw response.error
        const result = response.data
        return result ? normalizeConfig(result) : undefined
      }}
    />
  )
}

function MailboxEditor({
  inboxId,
  gmail = false,
  onDirtyChange,
  section,
  starterInstructions,
  onSectionChange,
  initial,
  reload,
}: Props & {
  initial: ClassificationConfig
  reload: () => Promise<ClassificationConfig | undefined>
}) {
  const [draft, setDraft] = useState(initial)
  const [saved, setSaved] = useState(initial)
  const [latest, setLatest] = useState<ClassificationConfig>()
  const [message, setMessage] = useState('')
  const [tab, setTab] = useState<MailboxSection>('instructions')
  const [instructionsDirty, setInstructionsDirty] = useState(false)
  const [confirmActivation, setConfirmActivation] = useState(false)
  const [permissionsOpened, setPermissionsOpened] = useState(
    section === 'permissions',
  )
  const targets = useClassificationTargets(inboxId)
  const queryClient = useQueryClient()
  const status = useClassificationStatus(inboxId)
  const save = useSaveClassification(inboxId)
  const activate = useActivateClassification(inboxId)
  const pause = useUpdateInbox()
  const permissionsDirty = JSON.stringify(draft) !== JSON.stringify(saved)
  const dirty = permissionsDirty || instructionsDirty
  const selectedTab = section ?? tab
  if (selectedTab === 'permissions' && !permissionsOpened)
    setPermissionsOpened(true)
  const sameSavedPermissions =
    JSON.stringify(initial.permissions) === JSON.stringify(saved.permissions) &&
    JSON.stringify(initial.assessments) === JSON.stringify(saved.assessments)
  if (
    initial.version !== saved.version &&
    (!permissionsDirty || sameSavedPermissions)
  ) {
    setSaved(initial)
    setDraft(
      permissionsDirty ? { ...draft, version: initial.version } : initial,
    )
  }
  const needsFirstSave = section === 'permissions' && saved.version === 0
  useEffect(() => {
    onDirtyChange?.(inboxId, dirty || needsFirstSave)
  }, [dirty, needsFirstSave, inboxId, onDirtyChange])
  const change = (next: ClassificationConfig) => {
    setDraft(next)
    setMessage('')
    setConfirmActivation(false)
  }
  const handleSave = async () => {
    setMessage('')
    try {
      const result = await save.mutateAsync(draft)
      setDraft(normalizeConfig(result))
      setSaved(normalizeConfig(result))
      setLatest(undefined)
      setMessage('Saved.')
      status.refetch()
    } catch (error) {
      setMessage(classificationRequestMessage(error))
    }
  }
  const active = status.data?.active === true
  return (
    <div className="sentry-block ph-no-capture space-y-6" data-private="true">
      {!section && (
        <nav
          aria-label="Mailbox settings"
          className="flex flex-wrap gap-1 border-b pb-4"
        >
          {(['instructions', 'permissions', 'preview'] as const).map(
            (value) => (
              <Button
                key={value}
                variant={selectedTab === value ? 'secondary' : 'ghost'}
                aria-current={selectedTab === value ? 'page' : undefined}
                onClick={() => {
                  setTab(value)
                  setConfirmActivation(false)
                }}
              >
                {
                  {
                    instructions: 'Instructions',
                    permissions: 'Actions',
                    preview: 'Preview',
                  }[value]
                }
              </Button>
            ),
          )}
        </nav>
      )}
      <div hidden={selectedTab !== 'instructions'}>
        <InstructionOverlays
          inboxId={inboxId}
          onDirtyChange={setInstructionsDirty}
          useStarter={starterInstructions}
        />
      </div>
      <div hidden={selectedTab !== 'permissions'} className="space-y-4">
        <fieldset disabled={save.isPending}>
          {permissionsOpened && (
            <MailboxPermissions
              inboxId={inboxId}
              config={draft}
              targets={targets.data}
              gmail={gmail}
              onChange={change}
              targetsFailed={targets.isError}
              onRetryTargets={() => targets.refetch()}
            />
          )}
        </fieldset>
        {targets.isError && (
          <div role="alert" className="space-y-2">
            <p>Your labels and folders couldn’t load.</p>
            <Button variant="outline" onClick={() => targets.refetch()}>
              Try again
            </Button>
          </div>
        )}
        {message && (
          <p
            role="status"
            className={`text-sm ${save.isError ? 'text-destructive' : 'text-muted-foreground'}`}
          >
            {message}
          </p>
        )}
        {save.error instanceof ClassificationRequestError &&
          save.error.status === 409 && (
            <div className="space-y-3 rounded-lg border p-4">
              <Button
                variant="outline"
                onClick={async () => {
                  try {
                    setLatest(await reload())
                  } catch (error) {
                    setMessage(classificationRequestMessage(error))
                  }
                }}
              >
                See the latest saved version
              </Button>
              {latest && (
                <>
                  <p className="text-sm font-medium">
                    Latest saved version
                  </p>
                  <fieldset disabled>
                    <MailboxPermissions
                      inboxId={inboxId}
                      config={latest}
                      targets={targets.data}
                      gmail={gmail}
                      onChange={() => {}}
                      readOnly
                    />
                  </fieldset>
                  <Button
                    variant="outline"
                    onClick={() => {
                      setDraft({ ...draft, version: latest.version })
                      setSaved(latest)
                      setLatest(undefined)
                      save.reset()
                      setMessage(
                        'Your changes are kept. Save to replace the saved version.',
                      )
                    }}
                  >
                    Keep my edits
                  </Button>
                </>
              )}
            </div>
          )}
        <div className="flex flex-wrap items-center gap-3">
          <Button
            disabled={
              save.isPending || (!permissionsDirty && saved.version > 0)
            }
            onClick={handleSave}
          >
            <Save className="size-4" />
            {save.isPending ? 'Saving…' : 'Save actions'}
          </Button>
          <Button
            variant="ghost"
            onClick={() =>
              queryClient.invalidateQueries({
                queryKey: queryKeys.classification(inboxId),
              })
            }
          >
            {gmail ? 'Reload labels' : 'Reload folders'}
          </Button>
        </div>
      </div>
      {selectedTab === 'preview' && (
        <Preview
          inboxId={inboxId}
          config={saved}
          targets={targets.data}
          dirty={dirty}
          reload={reload}
          onReview={() =>
            onSectionChange
              ? onSectionChange('instructions')
              : setTab('instructions')
          }
        />
      )}
      {(!section || selectedTab === 'processing') && (
        <section className="space-y-3">
          <h2 className="font-semibold">Processing</h2>
          <div className="space-y-4">
            {status.isLoading ? (
              <p role="status">Checking processing status…</p>
            ) : status.isError ? (
              <div className="space-y-2">
                <p role="alert">Processing status could not load.</p>
                <Button variant="outline" onClick={() => status.refetch()}>
                  Try again
                </Button>
              </div>
            ) : (
              <>
                <p className="text-sm">
                  {active
                    ? 'Processing is active for new email.'
                    : status.data?.setup_required
                      ? status.data.activation.ready
                        ? 'Ready to enable processing.'
                        : 'Finish setup to enable processing.'
                      : 'Automatic processing is paused.'}
                </p>
                {!active && !status.data?.activation.ready && (
                  <p className="text-sm text-muted-foreground">
                    {status.data?.activation.reason}
                  </p>
                )}
                {active && (
                  <Button
                    variant="outline"
                    disabled={pause.isPending}
                    onClick={() =>
                      pause.mutate(
                        { inboxId, data: { is_active: false } },
                        {
                          onSuccess: () => {
                            status.refetch()
                          },
                        },
                      )
                    }
                  >
                    {pause.isPending ? 'Pausing…' : 'Pause processing'}
                  </Button>
                )}
                {!active &&
                  status.data?.activation.ready &&
                  !confirmActivation && (
                    <Button
                      disabled={dirty || !saved.version}
                      onClick={() => setConfirmActivation(true)}
                    >
                      Enable processing…
                    </Button>
                  )}
                {confirmActivation && (
                  <div className="space-y-3 rounded-lg border p-4">
                    <p className="text-sm">
                      Enable processing for new email with your saved
                      instructions and permissions?
                    </p>
                    <div className="flex flex-wrap gap-2">
                      <Button
                        disabled={activate.isPending || dirty}
                        onClick={() =>
                          activate.mutate(
                            { expected_version: saved.version },
                            { onSuccess: () => setConfirmActivation(false) },
                          )
                        }
                      >
                        {activate.isPending
                          ? 'Enabling…'
                          : 'Enable for new email'}
                      </Button>
                      <Button
                        variant="outline"
                        onClick={() => setConfirmActivation(false)}
                      >
                        Cancel
                      </Button>
                    </div>
                  </div>
                )}
              </>
            )}
            {(activate.error || pause.error) && (
              <p role="alert" className="text-sm text-destructive">
                {classificationRequestMessage(activate.error || pause.error)}
              </p>
            )}
          </div>
        </section>
      )}
    </div>
  )
}
